import { useState, useEffect, useRef, useCallback } from 'react';
import { Timestamp } from '@firebase/firestore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { CommonButton } from './CommonButton';
import { CancelButton } from './CancelButton';
import { Enterprise } from '../type';
import { DialogMode } from '../type/DialogMode';
import { uploadImage } from '../service/imageService';

interface EnterpriseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (enterprise: Enterprise) => Promise<boolean>;
  enterprise?: Enterprise | null;
  mode: DialogMode;
}

export const EnterpriseDialog = ({
  open,
  onClose,
  onSubmit,
  enterprise,
  mode,
}: EnterpriseDialogProps) => {
  const [enterpriseData, setEnterpriseData] = useState<Enterprise>({
    enterpriseId: '',
    enterpriseName: '',
    logoUrl: null,
    createdAt: Timestamp.now()
  });
  const [logoImageFile, setLogoImageFile] = useState<File | null>(null);
  const [logoImageUrl, setLogoImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (enterprise && mode === DialogMode.EDIT) {
      setEnterpriseData(enterprise);
      setLogoImageUrl(enterprise.logoUrl || '');
      setLogoImageFile(null);
    } else {
      setEnterpriseData({
        enterpriseId: '',
        enterpriseName: '',
        logoUrl: null,
        createdAt: Timestamp.now()
      });
      setLogoImageUrl('');
      setLogoImageFile(null);
    }
    setError(null);
  }, [enterprise, mode, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!enterpriseData.enterpriseId.trim() || !enterpriseData.enterpriseName.trim()) {
      setError('기업 ID와 기업명은 필수 입력 항목입니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let finalLogoUrl = enterpriseData.logoUrl;
      
      if (logoImageFile) {
        try {
          setIsUploading(true);
          finalLogoUrl = await uploadImage(logoImageFile, 'enterprise-logos');
        } finally {
          setIsUploading(false);
        }
      }

      const finalEnterpriseData = {
        ...enterpriseData,
        logoUrl: finalLogoUrl
      };

      const success = await onSubmit(finalEnterpriseData);
      if (success) {
        onClose();
      }
    } catch (err) {
      setError('저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleLogoImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoImageUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleLogoImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {mode === DialogMode.CREATE ? '기업 등록' : '기업 수정'}
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              label="기업 ID"
              value={enterpriseData.enterpriseId}
              onChange={(e) => setEnterpriseData({ ...enterpriseData, enterpriseId: e.target.value })}
              placeholder="예: naver, kakao, musinsa"
              required
              disabled={mode === DialogMode.EDIT}
              helperText="고유한 식별자"
            />
            
            <TextField
              label="기업명"
              value={enterpriseData.enterpriseName}
              onChange={(e) => setEnterpriseData({ ...enterpriseData, enterpriseName: e.target.value })}
              placeholder="예: 네이버, 카카오, 무신사"
              required
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                로고 이미지
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#999',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={handleLogoImageClick}
              >
                {logoImageUrl ? (
                  <img 
                    src={logoImageUrl} 
                    alt="로고 미리보기" 
                    style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    로고 이미지를 클릭하여 업로드하세요
                  </Typography>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoImageChange}
                style={{ display: 'none' }}
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <CancelButton onClick={handleClose} disabled={loading} />
          <CommonButton
            type="submit"
            disabled={loading || isUploading}
            sx={{ minWidth: 100 }}
          >
            {loading || isUploading ? '저장 중...' : mode === DialogMode.CREATE ? '등록' : '수정'}
          </CommonButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}; 